import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { firestore } from '@/firebase';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from '@/config';
import {
  PlatformPreferences,
  PreferredVertical,
  RunHistoryItem,
  UserProfile,
} from '@/types';
import {
  ANONYMOUS_PLATFORM_PREFERENCES_STORAGE_KEY,
  clearAnonymousPlatformPreferences,
  clearPendingUserPlatformPreferences,
  getPendingUserPlatformPreferencesStorageKey,
  hasCompletedPlatformOnboarding,
  normalizePlatformPreferences,
  readAnonymousPlatformPreferences,
  readPendingUserPlatformPreferences,
  writeAnonymousPlatformPreferences,
  writePendingUserPlatformPreferences,
} from '@/lib/platformPreferences';
import { isBedrockOrgMember } from '@/lib/organizationMembership';

interface CreditContextType {
  profile: UserProfile | null;
  history: RunHistoryItem[];
  loading: boolean;
  profileLoading: boolean;
  profileLoadedFromServer: boolean;
  balanceLoading: boolean;
  historyLoading: boolean;
  error: string | null;
  effectivePlatformPreferences?: PlatformPreferences;
  fetchCreditData: () => void;
  dismissNewUser: () => Promise<void>;
  savePlatformOnboardingPreference: (
    preferredVertical: PreferredVertical,
  ) => PlatformPreferences;
  updatePlatformPreferences: (
    preferredVertical: PreferredVertical,
  ) => Promise<PlatformPreferences>;
}

interface PendingUserPlatformPreferences {
  userId: string;
  preferences: PlatformPreferences;
}

const CreditContext = createContext<CreditContextType | undefined>(undefined);

const PROFILE_CACHE_KEY = 'credit_profile_cache_v2';
const PROFILE_CACHE_TTL_MS = 5 * 60 * 1000;

const canUseSessionStorage =
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

function normalizeProfile(data: any): UserProfile {
  return {
    // Only /credits/balance supplies the credit number; leave undefined when
    // absent so merges never overwrite a real balance with a coerced 0.
    credits: typeof data?.credits === 'number' ? data.credits : undefined,
    plan: data?.plan ?? 'trial',
    billing_source: data?.billing_source ?? undefined,
    marketplace: data?.marketplace
      ? {
          status: String(data.marketplace.status ?? 'pending_activation'),
          block_reason: data.marketplace.block_reason ?? undefined,
          subscription_end_at: data.marketplace.subscription_end_at ?? undefined,
          access_expires_at: data.marketplace.access_expires_at ?? undefined,
          plan_id: data.marketplace.plan_id ?? undefined,
          plan_name: data.marketplace.plan_name ?? undefined,
          account_id: data.marketplace.account_id ?? undefined,
          entitlement_id: data.marketplace.entitlement_id ?? undefined,
          period_key: data.marketplace.period_key ?? undefined,
          period_start_at: data.marketplace.period_start_at ?? undefined,
          period_end_at: data.marketplace.period_end_at ?? undefined,
          included_credits: Number(data.marketplace.included_credits ?? 0),
          remaining_credits: Number(data.marketplace.remaining_credits ?? 0),
          reserved_credits: Number(data.marketplace.reserved_credits ?? 0),
          used_credits: Number(data.marketplace.used_credits ?? 0),
          management_url: data.marketplace.management_url ?? undefined,
        }
      : undefined,
    trial_end_date: data?.trial_end_date ?? undefined,
    org_id: data?.org_id ?? undefined,
    org_name: data?.org_name ?? undefined,
    org_trial_end_date: data?.org_trial_end_date ?? undefined,
    is_org_member: Boolean(data?.is_org_member),
    rates: data?.rates,
    admin_mode: data?.admin_mode !== undefined ? Boolean(data.admin_mode) : undefined,
    is_new_user: data?.is_new_user !== undefined ? Boolean(data.is_new_user) : undefined,
    platform_preferences: normalizePlatformPreferences(data?.platform_preferences),
    password_reset_required:
      data?.password_reset_required !== undefined
        ? Boolean(data.password_reset_required)
        : undefined,
    upload_entitlement: data?.upload_entitlement
      ? { can_upload_videos: Boolean(data.upload_entitlement.can_upload_videos) }
      : undefined,
  };
}

function mergeDefinedProfile(prev: UserProfile, nextPartial: UserProfile): UserProfile {
  const merged: UserProfile = { ...prev };
  (Object.keys(nextPartial) as Array<keyof UserProfile>).forEach((key) => {
    const value = nextPartial[key];
    if (value !== undefined) {
      merged[key] = value as any;
    }
  });
  return merged;
}

function creditScope(profile: UserProfile | null): string {
  return profile?.org_id ? `org:${profile.org_id}` : 'user';
}

function replaceProfilePreservingSameScopeCredits(
  prev: UserProfile | null,
  nextProfile: UserProfile
): UserProfile {
  if (
    prev?.credits === undefined ||
    nextProfile.credits !== undefined ||
    creditScope(prev) !== creditScope(nextProfile)
  ) {
    return nextProfile;
  }

  return { ...nextProfile, credits: prev.credits };
}

function readCachedProfile(): UserProfile | null {
  if (!canUseSessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.data || !parsed?.timestamp) return null;

    if (Date.now() - parsed.timestamp > PROFILE_CACHE_TTL_MS) {
      window.sessionStorage.removeItem(PROFILE_CACHE_KEY);
      return null;
    }

    return normalizeProfile(parsed.data);
  } catch (err) {
    console.warn('Failed to read cached profile', err);
    return null;
  }
}

function writeCachedProfile(profile: UserProfile) {
  if (!canUseSessionStorage) return;
  try {
    window.sessionStorage.setItem(
      PROFILE_CACHE_KEY,
      JSON.stringify({ data: profile, timestamp: Date.now() })
    );
  } catch (err) {
    console.warn('Failed to write cached profile', err);
  }
}

function clearProfileCache() {
  if (!canUseSessionStorage) return;
  try {
    window.sessionStorage.removeItem(PROFILE_CACHE_KEY);
  } catch (err) {
    console.warn('Failed to clear cached profile', err);
  }
}

export const CreditProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, getToken } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const profileRef = useRef<UserProfile | null>(null);
  const [history, setHistory] = useState<RunHistoryItem[]>([]);
  const [profileLoading, setProfileLoading] = useState<boolean>(true);
  const [profileLoadedFromServer, setProfileLoadedFromServer] =
    useState<boolean>(false);
  const profileLoadedForUserIdRef = useRef<string | null>(null);
  const [balanceLoading, setBalanceLoading] = useState<boolean>(false);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [anonymousPlatformPreferences, setAnonymousPlatformPreferences] =
    useState<PlatformPreferences | undefined>(readAnonymousPlatformPreferences);
  const [pendingUserPlatformPreferences, setPendingUserPlatformPreferences] =
    useState<PendingUserPlatformPreferences>();
  const preferenceSyncAttemptRef = useRef<string | null>(null);

  const persistProfile = useCallback(
    (
      data: UserProfile,
      options: { mergeDefined?: boolean; preserveSameScopeCredits?: boolean } = {}
    ) => {
      setProfile((prev) => {
        const next = options.mergeDefined && prev
          ? mergeDefinedProfile(prev, data)
          : options.preserveSameScopeCredits
            ? replaceProfilePreservingSameScopeCredits(prev, data)
            : data;
        profileRef.current = next;
        writeCachedProfile(next);
        return next;
      });
    },
    []
  );

  const fetchUserProfile = useCallback(async () => {
    if (!user) return;
    if (!profileRef.current) {
      setProfileLoading(true);
    }

    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication token not found.');

      const response = await fetch(`${API_BASE_URL}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to fetch user profile');
      }

      const payload = await response.json();
      profileLoadedForUserIdRef.current = user.uid;
      persistProfile(normalizeProfile(payload), { preserveSameScopeCredits: true });
      setProfileLoadedFromServer(true);
    } catch (err: any) {
      console.error('CreditContext profile error:', err);
      setError(err.message || 'Failed to fetch user profile');
    } finally {
      setProfileLoading(false);
    }
  }, [user, getToken, persistProfile]);

  const fetchCreditBalance = useCallback(async () => {
    if (!user) return;
    setBalanceLoading(true);

    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication token not found.');

      const response = await fetch(`${API_BASE_URL}/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to fetch credit balance');
      }

      const payload = await response.json();
      persistProfile(normalizeProfile(payload), { mergeDefined: true });
    } catch (err: any) {
      console.error('CreditContext balance error:', err);
      setError(err.message || 'Failed to fetch credit balance');
    } finally {
      setBalanceLoading(false);
    }
  }, [user, getToken, persistProfile]);

  const fetchHistoryData = useCallback(async () => {
    if (!user) return;
    setHistoryLoading(true);

    try {
      const token = await getToken();
      if (!token) throw new Error('Authentication token not found.');

      const response = await fetch(`${API_BASE_URL}/credits/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to fetch run history');
      }

      const historyPayload = await response.json();
      setHistory(historyPayload);
    } catch (err: any) {
      console.error('CreditContext history error:', err);
      setError(err.message || 'Failed to fetch run history');
    } finally {
      setHistoryLoading(false);
    }
  }, [user, getToken]);

  const fetchCreditData = useCallback(async () => {
    await Promise.allSettled([
      fetchUserProfile(),
      fetchCreditBalance(),
      fetchHistoryData(),
    ]);
  }, [fetchUserProfile, fetchCreditBalance, fetchHistoryData]);

  const dismissNewUser = useCallback(async () => {
    if (!user) return;
    // Update local state + cache immediately
    persistProfile({ is_new_user: false } as UserProfile, { mergeDefined: true });
    // Persist to Firestore in background
    try {
      await setDoc(doc(firestore, 'users', user.uid), { is_new_user: false }, { merge: true });
    } catch (err) {
      console.error('Failed to update is_new_user in Firestore:', err);
    }
  }, [user, persistProfile]);

  const updatePlatformPreferences = useCallback(
    async (preferredVertical: PreferredVertical): Promise<PlatformPreferences> => {
      if (!user) throw new Error('You must be signed in to update your preferences.');

      const token = await getToken();
      if (!token) throw new Error('Authentication token not found.');

      const response = await fetch(`${API_BASE_URL}/users/me/platform-preferences`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ preferred_vertical: preferredVertical }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to update platform preferences');
      }

      const payload = await response.json();
      const preferences = normalizePlatformPreferences(payload?.platform_preferences);
      if (!preferences) {
        throw new Error('The server returned invalid platform preferences.');
      }

      persistProfile(
        { platform_preferences: preferences } as UserProfile,
        { mergeDefined: true },
      );
      return preferences;
    },
    [user, getToken, persistProfile],
  );

  const savePlatformOnboardingPreference = useCallback(
    (preferredVertical: PreferredVertical): PlatformPreferences => {
      const userId = user?.uid;
      if (!userId) {
        const preferences = writeAnonymousPlatformPreferences(preferredVertical);
        setAnonymousPlatformPreferences(preferences);
        return preferences;
      }

      const preferences = writePendingUserPlatformPreferences(
        userId,
        preferredVertical,
      );
      setPendingUserPlatformPreferences({ userId, preferences });
      return preferences;
    },
    [user?.uid],
  );

  const discardAnonymousPlatformPreferences = useCallback(() => {
    clearAnonymousPlatformPreferences();
    setAnonymousPlatformPreferences(undefined);
  }, []);

  const discardPendingUserPlatformPreferences = useCallback((userId: string) => {
    clearPendingUserPlatformPreferences(userId);
    setPendingUserPlatformPreferences((current) =>
      current?.userId === userId ? undefined : current,
    );
  }, []);

  const currentUserPendingPlatformPreferences =
    user?.uid && pendingUserPlatformPreferences?.userId === user.uid
      ? pendingUserPlatformPreferences.preferences
      : undefined;

  const effectivePlatformPreferences = useMemo(() => {
    if (isBedrockOrgMember(profile?.org_id)) return undefined;

    const serverPreferences = profile?.platform_preferences;
    if (hasCompletedPlatformOnboarding(serverPreferences)) {
      return serverPreferences;
    }
    if (hasCompletedPlatformOnboarding(currentUserPendingPlatformPreferences)) {
      return currentUserPendingPlatformPreferences;
    }
    if (hasCompletedPlatformOnboarding(anonymousPlatformPreferences)) {
      return anonymousPlatformPreferences;
    }
    return (
      serverPreferences ??
      currentUserPendingPlatformPreferences ??
      anonymousPlatformPreferences
    );
  }, [
    anonymousPlatformPreferences,
    currentUserPendingPlatformPreferences,
    profile,
  ]);

  useEffect(() => {
    const userId = user?.uid;
    if (!userId) {
      setPendingUserPlatformPreferences(undefined);
      return;
    }

    const preferences = readPendingUserPlatformPreferences(userId);
    setPendingUserPlatformPreferences(
      preferences ? { userId, preferences } : undefined,
    );
  }, [user?.uid]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const userId = user?.uid;
    const handleStorage = (event: StorageEvent) => {
      if (event.key === ANONYMOUS_PLATFORM_PREFERENCES_STORAGE_KEY) {
        setAnonymousPlatformPreferences(readAnonymousPlatformPreferences());
      }
      if (
        userId &&
        event.key === getPendingUserPlatformPreferencesStorageKey(userId)
      ) {
        const preferences = readPendingUserPlatformPreferences(userId);
        setPendingUserPlatformPreferences(
          preferences ? { userId, preferences } : undefined,
        );
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [user?.uid]);

  useEffect(() => {
    preferenceSyncAttemptRef.current = null;
  }, [user?.uid]);

  // Promote a completed locally saved onboarding choice after verified auth
  // has loaded the authoritative profile. Authenticated fallbacks are scoped
  // to their user; the unscoped live-demo fallback can seed the first login.
  useEffect(() => {
    const userId = user?.uid;
    const pendingPreferences = currentUserPendingPlatformPreferences;
    const hasPendingUserPreference =
      hasCompletedPlatformOnboarding(pendingPreferences);
    const preferencesToPromote = hasPendingUserPreference
      ? pendingPreferences
      : hasCompletedPlatformOnboarding(anonymousPlatformPreferences)
        ? anonymousPlatformPreferences
        : undefined;
    const preferenceSource = hasPendingUserPreference ? 'user' : 'anonymous';

    if (
      !userId ||
      !user?.emailVerified ||
      !profileLoadedFromServer ||
      profileLoadedForUserIdRef.current !== userId ||
      !preferencesToPromote?.preferred_vertical
    ) {
      return;
    }

    if (
      isBedrockOrgMember(profile?.org_id) ||
      hasCompletedPlatformOnboarding(profile?.platform_preferences)
    ) {
      discardPendingUserPlatformPreferences(userId);
      discardAnonymousPlatformPreferences();
      return;
    }

    const attemptKey = [
      userId,
      preferenceSource,
      preferencesToPromote.preferred_vertical,
      preferencesToPromote.onboarding_version,
      preferencesToPromote.onboarding_completed_at,
    ].join(':');
    if (preferenceSyncAttemptRef.current === attemptKey) return;
    preferenceSyncAttemptRef.current = attemptKey;

    void updatePlatformPreferences(
      preferencesToPromote.preferred_vertical,
    )
      .then(() => {
        discardPendingUserPlatformPreferences(userId);
        discardAnonymousPlatformPreferences();
      })
      .catch((syncError) => {
        // Keep the local value so the experience stays personalized and a
        // later authenticated session can retry without asking again.
        console.warn(
          'Failed to promote locally saved platform preferences:',
          syncError,
        );
      });
  }, [
    anonymousPlatformPreferences,
    discardAnonymousPlatformPreferences,
    discardPendingUserPlatformPreferences,
    currentUserPendingPlatformPreferences,
    profile?.org_id,
    profile?.platform_preferences,
    profileLoadedFromServer,
    updatePlatformPreferences,
    user?.emailVerified,
    user?.uid,
  ]);

  useEffect(() => {
    if (user) {
      profileLoadedForUserIdRef.current = null;
      setProfileLoadedFromServer(false);
      setError(null);
      setHistory([]);
      setBalanceLoading(false);
      setHistoryLoading(false);
      const cachedProfile = readCachedProfile();
      if (cachedProfile) {
        persistProfile(cachedProfile);
        setProfileLoading(false);
      } else {
        profileRef.current = null;
        setProfile(null);
        setProfileLoading(true);
      }

      // App startup only needs lightweight profile/org metadata.
      // Balance and credit history stay opt-in because they can trigger
      // expensive ledger scans that slow unrelated route transitions.
      fetchUserProfile();
    } else {
      profileLoadedForUserIdRef.current = null;
      setProfile(null);
      profileRef.current = null;
      setHistory([]);
      setProfileLoading(false);
      setProfileLoadedFromServer(false);
      setBalanceLoading(false);
      setHistoryLoading(false);
      setError(null);
      clearProfileCache();
    }
  }, [user, fetchUserProfile, persistProfile]);

  return (
    <CreditContext.Provider
      value={{
        profile,
        history,
        loading: profileLoading,
        profileLoading,
        profileLoadedFromServer,
        balanceLoading,
        historyLoading,
        error,
        effectivePlatformPreferences,
        fetchCreditData,
        dismissNewUser,
        savePlatformOnboardingPreference,
        updatePlatformPreferences,
      }}
    >
      {children}
    </CreditContext.Provider>
  );
};

export const useCredits = () => {
  const context = useContext(CreditContext);
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditProvider');
  }
  return context;
};
